import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SafeAreaView, View, Image, TouchableOpacity, Text, ScrollView, StyleSheet, ActivityIndicator, DeviceEventEmitter } from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { BottomNavBar } from '../../components/BottomNavBar';
import { TaskItem } from '../../components/TaskItem';
import { API_URL } from '../../config';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { RootStackParamList } from '../../types/navigation';
import { ChecklistCardScroll } from '../../components/ChecklistCardScroll';
import { PERSONAL_TASKS_CONFIG, PASSWORDS_TASKS_CONFIG, CONTACTS_TASKS_CONFIG, EXPENSES_TASKS_CONFIG, LOYALTY_POINTS_TASKS_CONFIG } from '../../constants/taskIds';
import { mapTaskIdToKey } from '../../utils/taskKey';
import { vaultService } from '../../services/vaultService';
import { contactService } from '../../services/contactService';
import { expenseService } from '../../services/expenseService';
import { loyaltyPointService } from '../../services/loyaltyPointService';
import { documentStorageService } from '../../services/documentStorageService';
import { MEDICAL_TASKS_CONFIG, FINANCE_TASKS_CONFIG, PROPERTY_TASKS_CONFIG, FINAL_WISHES_TASKS_CONFIG, HEALTHCARE_DIRECTIVES_TASKS_CONFIG, ESTATE_PLANNING_TASKS_CONFIG, CARE_PREFERENCES_TASKS_CONFIG } from '../../constants/taskIds';
import { useUserSelection } from '../../contexts/UserSelectionContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { guardVaultEdit } from '../../utils/vaultEditGate';
import { UpgradeBanner } from '../../components/UpgradeBanner';

import { createLogger } from '../../utils/logger';
const logger = createLogger('PersonalChecklist');

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export const PersonalChecklistScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { selectedUserId, isViewingOthersData, availablePrimaryUsers } = useUserSelection();
  const { canViewOwnData, isLoading: subscriptionLoading, subscription, canEditData } = useSubscription();
  const [completedTasks, setCompletedTasks] = useState<number[]>([]);

  // Debug logging for subscription status
  logger.info('[PersonalChecklist] canViewOwnData:', canViewOwnData, 'subscriptionLoading:', subscriptionLoading, 'isViewingOthersData:', isViewingOthersData);
  logger.info('[PersonalChecklist] subscription data:', JSON.stringify(subscription));
  const [loading, setLoading] = useState(true);

  // Get the name of the user whose data we're viewing
  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('free');
  const [categoryProgress, setCategoryProgress] = useState<any[]>([]);
  const [completedPasswordTasks, setCompletedPasswordTasks] = useState<string[]>([]);
  const [completedContactTasks, setCompletedContactTasks] = useState<string[]>([]);
  const [completedExpenseTasks, setCompletedExpenseTasks] = useState<string[]>([]);
  const [completedLoyaltyPointTasks, setCompletedLoyaltyPointTasks] = useState<string[]>([]);
  const [completedMedicalTasks, setCompletedMedicalTasks] = useState<string[]>([]);
  const [completedFinanceTasks, setCompletedFinanceTasks] = useState<string[]>([]);
  const [completedPropertyTasks, setCompletedPropertyTasks] = useState<string[]>([]);
  const [completedFinalWishesTasks, setCompletedFinalWishesTasks] = useState<string[]>([]);
  const [completedHealthcareDirectivesTasks, setCompletedHealthcareDirectivesTasks] = useState<string[]>([]);
  const [completedEstatePlanningTasks, setCompletedEstatePlanningTasks] = useState<string[]>([]);
  const [completedCarePreferencesTasks, setCompletedCarePreferencesTasks] = useState<string[]>([]);
  const [completedDocumentTasks, setCompletedDocumentTasks] = useState<string[]>([]);
  const cacheRef = useRef<{ completed: number[]; timestamp: number } | null>(null);
  const CACHE_DURATION = 60000; // 60 seconds

  const setLoadingWithLog = (value: boolean) => {
    logger.debug(`PersonalChecklistScreen: Setting loading to ${value}`);
    setLoading(value);
  };

  const fetchCompletedTasks = async (useCache = true) => {
    const now = Date.now();
    if (useCache && cacheRef.current && (now - cacheRef.current.timestamp < CACHE_DURATION)) {
      setCompletedTasks(cacheRef.current.completed);
      setLoadingWithLog(false);
      return;
    }
    setLoadingWithLog(true);
    
    // Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      logger.debug('fetchCompletedTasks timeout - forcing loading to false');
      setLoadingWithLog(false);
      setCompletedTasks([]);
    }, 5000); // 5 second timeout
    
    try {
      const token = await getAuthToken();
      if (!token) {
        // Demo mode or no token - set empty completed tasks and continue
        setCompletedTasks([]);
        setLoadingWithLog(false);
        clearTimeout(timeoutId);
        return;
      }
      
      // Fetch completed tasks from the correct endpoint (with optional targetUserId for viewing other user's data)
      try {
        const url = isViewingOthersData && selectedUserId
          ? `${API_URL}/api/tasks/user/completed-tasks?targetUserId=${selectedUserId}`
          : `${API_URL}/api/tasks/user/completed-tasks`;
        const completedRes = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        let completed: number[] = [];
        if (completedRes.ok) {
          const completedData = await completedRes.json();
          logger.debug('API /api/tasks/user/completed-tasks response:', completedData, isViewingOthersData ? `(viewing ${selectedUserId})` : '');
          completed = (completedData.completedTaskIds || [])
            .map((id: any) => Number(id))
            .filter((id: number) => !isNaN(id));
        }
        setCompletedTasks(completed);
        cacheRef.current = { completed, timestamp: now };
      } catch (apiError) {
        logger.debug('API endpoint not available, using empty completed tasks');
        setCompletedTasks([]);
        cacheRef.current = { completed: [], timestamp: now };
      }
    } catch (error) {
      logger.debug('Error in fetchCompletedTasks:', error);
      setCompletedTasks([]);
      cacheRef.current = { completed: [], timestamp: now };
    } finally {
      setLoadingWithLog(false);
      clearTimeout(timeoutId);
    }
  };

  const refreshTasks = () => {
    cacheRef.current = null;
    fetchCompletedTasks(false);
  };

  useFocusEffect(
    useCallback(() => {
      logger.debug('PersonalChecklistScreen: useFocusEffect triggered');
      refreshTasks();
      fetchPasswordCompletion();
      fetchContactCompletion();
      fetchExpenseCompletion();
      fetchLoyaltyPointCompletion();
      fetchDocumentCompletion();
      // Medical, finance, property, final wishes, and final care completion is derived from completedTasks
      // These will be triggered by the useEffect when completedTasks changes
      return () => {};
    }, [])
  );

  // Listen for Orion chat close event and refresh checklist
  useEffect(() => {
    const handleRefresh = () => {
      logger.debug('PersonalChecklistScreen: Received refreshChecklist event from Orion');
      // Force refresh by clearing cache
      cacheRef.current = null;
      fetchCompletedTasks(false);
      fetchPasswordCompletion();
      fetchContactCompletion();
      fetchExpenseCompletion();
      fetchLoyaltyPointCompletion();
      fetchDocumentCompletion();
    };

    const subscription = DeviceEventEmitter.addListener('refreshChecklist', handleRefresh);

    return () => {
      subscription.remove();
    };
  }, []);

  const fetchSubscriptionStatus = async () => {
    // TEMPORARY: Set as paid user for development/testing
    setSubscriptionStatus('cloud');
    /* COMMENTED OUT - Original API logic
    try {
      const token = await getAuthToken();
      if (!token) {
        setSubscriptionStatus('free');
        return;
      }
      const response = await fetch(`${API_URL}/auth/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        setSubscriptionStatus('free');
        return;
      }
      const data = await response.json();
      setSubscriptionStatus(data.tier || 'free');
    } catch (e) {
      setSubscriptionStatus('free');
    }
    */
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  // Fallback to ensure loading doesn't get stuck
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (loading) {
        logger.debug('PersonalChecklistScreen: Fallback timer triggered - forcing loading to false');
        setLoadingWithLog(false);
      }
    }, 10000); // 10 second fallback

    return () => clearTimeout(fallbackTimer);
  }, [loading]);

  const fetchCategoryProgress = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/api/tasks/category-progress`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCategoryProgress(data);
      } else {
        setCategoryProgress([]);
      }
    } catch (e) {
      setCategoryProgress([]);
    }
  };

  const fetchPasswordCompletion = async () => {
    try {
      // For password tasks, determine completion based on actual password data
      const allPasswords = await vaultService.getAllPasswords();
      const completedPasswordTasks: string[] = [];
      
      PASSWORDS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryPasswords = allPasswords.filter(p => p.category.toLowerCase() === task.category.toLowerCase());
          if (categoryPasswords.length > 0) {
            completedPasswordTasks.push(task.id.toString());
          }
        }
      });
      
      setCompletedPasswordTasks(completedPasswordTasks);
    } catch (error) {
      logger.error('Error fetching completed password tasks:', error);
      setCompletedPasswordTasks([]);
    }
  };

  // Fetch contact completion status
  const fetchContactCompletion = async () => {
    try {
      // For contact tasks, determine completion based on actual contact data
      const allContacts = await contactService.getAllContacts();
      const completedContactTasks: string[] = [];
      
      CONTACTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryContacts = allContacts.filter(c => c.category.toLowerCase() === task.category.toLowerCase());
          if (categoryContacts.length > 0) {
            completedContactTasks.push(task.id.toString());
          }
        }
      });
      
      setCompletedContactTasks(completedContactTasks);
    } catch (error) {
      logger.error('Error fetching completed contact tasks (using fallback):', error);
      // Gracefully handle API not available - use empty array
      setCompletedContactTasks([]);
    }
  };

  // Fetch expense completion status
  const fetchExpenseCompletion = async () => {
    try {
      // For expense tasks, determine completion based on actual expense data
      const allExpenses = await expenseService.getAllExpenses();
      const completedExpenseTasks: string[] = [];
      
      EXPENSES_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryExpenses = allExpenses.filter(e => e.category.toLowerCase() === task.category.toLowerCase());
          if (categoryExpenses.length > 0) {
            completedExpenseTasks.push(task.id.toString());
          }
        }
      });
      
      setCompletedExpenseTasks(completedExpenseTasks);
    } catch (error) {
      logger.error('Error fetching completed expense tasks (using fallback):', error);
      // Gracefully handle API not available - use empty array
      setCompletedExpenseTasks([]);
    }
  };

  // Fetch loyalty point completion status
  const fetchLoyaltyPointCompletion = async () => {
    try {
      // For loyalty point tasks, determine completion based on actual loyalty point data
      const allLoyaltyPoints = await loyaltyPointService.getAllLoyaltyPoints();
      const completedLoyaltyPointTasks: string[] = [];
      
      LOYALTY_POINTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryLoyaltyPoints = allLoyaltyPoints.filter(lp => lp.category.toLowerCase() === task.category.toLowerCase());
          if (categoryLoyaltyPoints.length > 0) {
            completedLoyaltyPointTasks.push(task.id.toString());
          }
        }
      });
      
      setCompletedLoyaltyPointTasks(completedLoyaltyPointTasks);
    } catch (error) {
      logger.error('Error fetching completed loyalty point tasks (using fallback):', error);
      // Gracefully handle API not available - use empty array
      setCompletedLoyaltyPointTasks([]);
    }
  };

  // Fetch medical completion status (using FinalCareChecklistScreen pattern)
  const fetchMedicalCompletion = async () => {
    try {
      // Mirror FinalCareChecklistScreen: compute from completedTasks ∩ MEDICAL_TASKS_CONFIG
      const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
      const completedMedical = completedTasks
        .filter(id => medicalIds.has(Number(id)))
        .map(id => id.toString());

      setCompletedMedicalTasks(completedMedical);
    } catch (error: any) {
      logger.error('Error calculating completed medical tasks:', error);
      setCompletedMedicalTasks([]);
    }
  };

  // Fetch finance completion status (using FinalCareChecklistScreen pattern)
  const fetchFinanceCompletion = async () => {
    try {
      // Mirror FinalCareChecklistScreen: compute from completedTasks ∩ FINANCE_TASKS_CONFIG
      const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinance = completedTasks
        .filter(id => financeIds.has(Number(id)))
        .map(id => id.toString());

      setCompletedFinanceTasks(completedFinance);
    } catch (error: any) {
      logger.error('Error calculating completed finance tasks:', error);
      setCompletedFinanceTasks([]);
    }
  };

  // Fetch property completion status (using FinalCareChecklistScreen pattern)
  const fetchPropertyCompletion = async () => {
    try {
      // Mirror FinalCareChecklistScreen: compute from completedTasks ∩ PROPERTY_TASKS_CONFIG
      const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
      const completedProperty = completedTasks
        .filter(id => propertyIds.has(Number(id)))
        .map(id => id.toString());

      setCompletedPropertyTasks(completedProperty);
    } catch (error: any) {
      logger.error('Error calculating completed property tasks:', error);
      setCompletedPropertyTasks([]);
    }
  };

  // Fetch final wishes completion status (using FinalCareChecklistScreen pattern)
  const fetchFinalWishesCompletion = async () => {
    try {
      // Mirror FinalCareChecklistScreen: compute from completedTasks ∩ FINAL_WISHES_TASKS_CONFIG
      const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinalWishes = completedTasks
        .filter(id => finalWishesIds.has(Number(id)))
        .map(id => id.toString());

      setCompletedFinalWishesTasks(completedFinalWishes);
    } catch (error: any) {
      logger.error('Error calculating completed final wishes tasks:', error);
      setCompletedFinalWishesTasks([]);
    }
  };

  // Fetch healthcare directives completion status
  const fetchHealthcareDirectivesCompletion = async () => {
    try {
      const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedHealthcareDirectives = completedTasks
        .filter(id => healthcareDirectivesIds.has(Number(id)))
        .map(id => id.toString());
      setCompletedHealthcareDirectivesTasks(completedHealthcareDirectives);
    } catch (error: any) {
      logger.error('Error calculating completed healthcare directives tasks:', error);
      setCompletedHealthcareDirectivesTasks([]);
    }
  };

  // Fetch estate planning completion status
  const fetchEstatePlanningCompletion = async () => {
    try {
      const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
      const completedEstatePlanning = completedTasks
        .filter(id => estatePlanningIds.has(Number(id)))
        .map(id => id.toString());
      setCompletedEstatePlanningTasks(completedEstatePlanning);
    } catch (error: any) {
      logger.error('Error calculating completed estate planning tasks:', error);
      setCompletedEstatePlanningTasks([]);
    }
  };

  // Fetch care preferences completion status
  const fetchCarePreferencesCompletion = async () => {
    try {
      const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCarePreferences = completedTasks
        .filter(id => carePreferencesIds.has(Number(id)))
        .map(id => id.toString());
      setCompletedCarePreferencesTasks(completedCarePreferences);
    } catch (error: any) {
      logger.error('Error calculating completed care preferences tasks:', error);
      setCompletedCarePreferencesTasks([]);
    }
  };

  // Fetch document completion status
  const fetchDocumentCompletion = async () => {
    try {
      const allDocuments = await documentStorageService.listDocuments();
      const completedDocumentTasksList: string[] = [];

      const DOCUMENT_CATEGORIES = ['personal', 'legal', 'medical', 'financial', 'property', 'insurance', 'digital', 'other'];
      const DOCUMENT_TASK_IDS = ['dm1', 'dm2', 'dm3', 'dm4', 'dm5', 'dm6', 'dm7', 'dm8'];

      DOCUMENT_CATEGORIES.forEach((category, index) => {
        const categoryDocuments = allDocuments.filter(doc => doc.category?.toLowerCase() === category);
        if (categoryDocuments.length > 0) {
          completedDocumentTasksList.push(DOCUMENT_TASK_IDS[index]);
        }
      });

      setCompletedDocumentTasks(completedDocumentTasksList);
    } catch (error) {
      logger.error('Error fetching completed document tasks:', error);
      setCompletedDocumentTasks([]);
    }
  };

  useEffect(() => {
    fetchCategoryProgress();
  }, []);

  // Refetch data when selectedUserId changes (viewing different user's data)
  useEffect(() => {
    if (selectedUserId) {
      logger.debug('PersonalChecklistScreen: selectedUserId changed, refetching...', selectedUserId);
      cacheRef.current = null; // Clear cache to force refetch
      fetchCompletedTasks(false);
    }
  }, [selectedUserId]);

  // Recalculate medical, finance, property, final wishes, and new category completion when completedTasks changes
  useEffect(() => {
    if (completedTasks.length >= 0) { // Only run when completedTasks has been loaded
      fetchMedicalCompletion();
      fetchFinanceCompletion();
      fetchPropertyCompletion();
      fetchFinalWishesCompletion();
      fetchHealthcareDirectivesCompletion();
      fetchEstatePlanningCompletion();
      fetchCarePreferencesCompletion();
    }
  }, [completedTasks]);

  // Recalculate categories when password and contact completion status changes
  useEffect(() => {
    // This will trigger a re-render of the categories with updated password and contact completion data
  }, [completedPasswordTasks, completedContactTasks, completedExpenseTasks, completedLoyaltyPointTasks, completedMedicalTasks, completedFinanceTasks, completedPropertyTasks, completedFinalWishesTasks, completedHealthcareDirectivesTasks, completedEstatePlanningTasks, completedCarePreferencesTasks, completedDocumentTasks, categoryProgress]);

  const mappedCategories = [
    { id: 'all', name: 'All Tasks', icon: require('../../assets/icons/all-tasks.png') },
    { id: 'personal', name: 'Personal', icon: require('../../assets/icons/personal.png') },
    { id: 'medical', name: 'Medical', icon: require('../../assets/icons/medical.png') },
    { id: 'finance', name: 'Finance', icon: require('../../assets/icons/financial.png') },
    { id: 'property', name: 'Property', icon: require('../../assets/icons/property.png') },
    { id: 'finalWishes', name: 'Final Wishes', icon: require('../../assets/icons/final-wishes.png') },
    { id: 'healthcareDirectives', name: 'Healthcare Directives', icon: require('../../assets/icons/healthcare.png') },
    { id: 'estatePlanning', name: 'Estate Planning', icon: require('../../assets/icons/estate-planning.png') },
    { id: 'carePreferences', name: 'Care Preferences', icon: require('../../assets/icons/final-care.png') },
    { id: 'passwords', name: 'Passwords', icon: require('../../assets/icons/password.png') },
    { id: 'contacts', name: 'Contacts', icon: require('../../assets/icons/all-contacts.png') },
    { id: 'expenses', name: 'Expenses', icon: require('../../assets/icons/expenses.png') },
    { id: 'documents', name: 'Documents', icon: require('../../assets/icons/folder.png') },
    { id: 'loyaltyPoints', name: 'Loyalty Points', icon: require('../../assets/icons/loyalty.png') },
  ].map(cat => {
    if (cat.id === 'all') {
      // For all category, calculate total completion across all categories
      const totalAllTasks = PERSONAL_TASKS_CONFIG.length + MEDICAL_TASKS_CONFIG.length + FINANCE_TASKS_CONFIG.length +
                           PROPERTY_TASKS_CONFIG.length + FINAL_WISHES_TASKS_CONFIG.length +
                           HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length + ESTATE_PLANNING_TASKS_CONFIG.length + CARE_PREFERENCES_TASKS_CONFIG.length +
                           PASSWORDS_TASKS_CONFIG.length + CONTACTS_TASKS_CONFIG.length + EXPENSES_TASKS_CONFIG.length +
                           LOYALTY_POINTS_TASKS_CONFIG.length + 8; // 8 document categories
      // For personal, use intersection logic
      const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
      const completedPersonalTasksCount = completedTasks.filter(id => personalIds.has(Number(id))).length;

      // For medical, use intersection logic
      const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
      const completedMedicalTasksCount = completedTasks.filter(id => medicalIds.has(Number(id))).length;

      // For finance, use intersection logic
      const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinanceTasksCount = completedTasks.filter(id => financeIds.has(Number(id))).length;

      // For property, use intersection logic
      const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
      const completedPropertyTasksCount = completedTasks.filter(id => propertyIds.has(Number(id))).length;

      // For final wishes, use intersection logic
      const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinalWishesTasksCount = completedTasks.filter(id => finalWishesIds.has(Number(id))).length;

      // For healthcare directives, use intersection logic
      const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedHealthcareDirectivesTasksCount = completedTasks.filter(id => healthcareDirectivesIds.has(Number(id))).length;

      // For estate planning, use intersection logic
      const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
      const completedEstatePlanningTasksCount = completedTasks.filter(id => estatePlanningIds.has(Number(id))).length;

      // For care preferences, use intersection logic
      const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCarePreferencesTasksCount = completedTasks.filter(id => carePreferencesIds.has(Number(id))).length;

      const completedAllTasks = completedPersonalTasksCount + completedMedicalTasksCount + completedFinanceTasksCount +
                               completedPropertyTasksCount + completedFinalWishesTasksCount +
                               completedHealthcareDirectivesTasksCount + completedEstatePlanningTasksCount + completedCarePreferencesTasksCount +
                               completedPasswordTasks.length + completedContactTasks.length + completedExpenseTasks.length +
                               completedLoyaltyPointTasks.length + completedDocumentTasks.length;
      return { ...cat, count: completedAllTasks, totalTasks: totalAllTasks };
    } else if (cat.id === 'personal') {
      // For personal category, compute from completedTasks intersecting PERSONAL_TASKS_CONFIG (same as FinalCareChecklistScreen)
      const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
      const completedPersonalTasksCount = completedTasks.filter(id => personalIds.has(Number(id))).length;
      const totalPersonalTasks = PERSONAL_TASKS_CONFIG.length;
      return { ...cat, count: completedPersonalTasksCount, totalTasks: totalPersonalTasks };
    } else if (cat.id === 'medical') {
      // For medical category, compute from completedTasks intersecting MEDICAL_TASKS_CONFIG (same as FinalCareChecklistScreen)
      const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
      const completedMedicalTasksCount = completedTasks.filter(id => medicalIds.has(Number(id))).length;
      const totalMedicalTasks = MEDICAL_TASKS_CONFIG.length;
      return { ...cat, count: completedMedicalTasksCount, totalTasks: totalMedicalTasks };
    } else if (cat.id === 'finance') {
      // For finance category, compute from completedTasks intersecting FINANCE_TASKS_CONFIG (same as FinalCareChecklistScreen)
      const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinanceTasksCount = completedTasks.filter(id => financeIds.has(Number(id))).length;
      const totalFinanceTasks = FINANCE_TASKS_CONFIG.length;
      return { ...cat, count: completedFinanceTasksCount, totalTasks: totalFinanceTasks };
    } else if (cat.id === 'property') {
      // For property category, compute from completedTasks intersecting PROPERTY_TASKS_CONFIG (same as FinalCareChecklistScreen)
      const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
      const completedPropertyTasksCount = completedTasks.filter(id => propertyIds.has(Number(id))).length;
      const totalPropertyTasks = PROPERTY_TASKS_CONFIG.length;
      return { ...cat, count: completedPropertyTasksCount, totalTasks: totalPropertyTasks };
    } else if (cat.id === 'finalWishes') {
      // Mirror FinalCareChecklistScreen: use intersection of completedTasks with FINAL_WISHES_TASKS_CONFIG
      const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinalWishesTasksCount = completedTasks.filter(id => finalWishesIds.has(Number(id))).length;
      const totalFinalWishesTasks = FINAL_WISHES_TASKS_CONFIG.length;
      return { ...cat, count: completedFinalWishesTasksCount, totalTasks: totalFinalWishesTasks };
    } else if (cat.id === 'healthcareDirectives') {
      const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedHealthcareDirectivesTasksCount = completedTasks.filter(id => healthcareDirectivesIds.has(Number(id))).length;
      const totalHealthcareDirectivesTasks = HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length;
      return { ...cat, count: completedHealthcareDirectivesTasksCount, totalTasks: totalHealthcareDirectivesTasks };
    } else if (cat.id === 'estatePlanning') {
      const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
      const completedEstatePlanningTasksCount = completedTasks.filter(id => estatePlanningIds.has(Number(id))).length;
      const totalEstatePlanningTasks = ESTATE_PLANNING_TASKS_CONFIG.length;
      return { ...cat, count: completedEstatePlanningTasksCount, totalTasks: totalEstatePlanningTasks };
    } else if (cat.id === 'carePreferences') {
      const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCarePreferencesTasksCount = completedTasks.filter(id => carePreferencesIds.has(Number(id))).length;
      const totalCarePreferencesTasks = CARE_PREFERENCES_TASKS_CONFIG.length;
      return { ...cat, count: completedCarePreferencesTasksCount, totalTasks: totalCarePreferencesTasks };
    } else if (cat.id === 'passwords') {
      // For passwords category, use password data to calculate progress
      const completedPasswordTasksCount = completedPasswordTasks.length;
      const totalPasswordTasks = PASSWORDS_TASKS_CONFIG.length;
      return { ...cat, count: completedPasswordTasksCount, totalTasks: totalPasswordTasks };
    } else if (cat.id === 'contacts') {
      // For contacts category, use contact data to calculate progress
      const completedContactTasksCount = completedContactTasks.length;
      const totalContactTasks = CONTACTS_TASKS_CONFIG.length;
      return { ...cat, count: completedContactTasksCount, totalTasks: totalContactTasks };
    } else if (cat.id === 'expenses') {
      // For expenses category, use expense data to calculate progress
      const completedExpenseTasksCount = completedExpenseTasks.length;
      const totalExpenseTasks = EXPENSES_TASKS_CONFIG.length;
      return { ...cat, count: completedExpenseTasksCount, totalTasks: totalExpenseTasks };
    } else if (cat.id === 'loyaltyPoints') {
      // For loyalty points category, use loyalty point data to calculate progress
      const completedLoyaltyPointTasksCount = completedLoyaltyPointTasks.length;
      const totalLoyaltyPointTasks = LOYALTY_POINTS_TASKS_CONFIG.length;
      return { ...cat, count: completedLoyaltyPointTasksCount, totalTasks: totalLoyaltyPointTasks };
    } else if (cat.id === 'documents') {
      // For documents category, use document data to calculate progress
      const completedDocumentTasksCount = completedDocumentTasks.length;
      const totalDocumentTasks = 8; // 8 document categories: personal, legal, medical, financial, property, insurance, digital, other
      return { ...cat, count: completedDocumentTasksCount, totalTasks: totalDocumentTasks };
    } else {
      // For other categories, use backend progress
      const progress = categoryProgress.find(p => p.categoryId === cat.id) || { completedCount: 0, totalCount: 0 };
      return { ...cat, count: progress.completedCount, totalTasks: progress.totalCount };
    }
  });

  // Debug logs for completedTasks and task completion status
  useEffect(() => {
    logger.debug('=== PERSONAL DEBUG ===');
    logger.debug('completedTasks state updated:', completedTasks);
    logger.debug('PERSONAL_TASKS_CONFIG ids:', PERSONAL_TASKS_CONFIG.map(t => t.id));
    const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
    const completedPersonalCount = completedTasks.filter(id => personalIds.has(Number(id))).length;
    logger.debug('Completed personal count for card:', completedPersonalCount);
    logger.debug('Total personal tasks:', PERSONAL_TASKS_CONFIG.length);
    PERSONAL_TASKS_CONFIG.forEach(task => {
      logger.debug(`Task: ${task.title}, id: ${task.id}, isCompleted: ${completedTasks.includes(Number(task.id))}`);
    });
    logger.debug('=== END PERSONAL DEBUG ===');
  }, [completedTasks]);

  const renderTaskItem = (task: any) => {
    const taskId = Number(task.id);
    // CRITICAL: Pure intersection-based completion check
    const isCompleted = completedTasks.includes(taskId);
    let showButton = true;
    let buttonText = '';
    let onPress: (() => void) = () => {};
    let buttonColor = '#29BFC0';

    // Determine if tasks should be disabled (user has no active subscription and not viewing others' data)
    // Also consider disabled if subscription is still loading
    const isTaskDisabled = (!canViewOwnData && !isViewingOthersData) || subscriptionLoading;
    logger.debug('[PersonalChecklist] Task disabled check - canViewOwnData:', canViewOwnData, 'isViewingOthersData:', isViewingOthersData, 'isTaskDisabled:', isTaskDisabled);

    if (subscriptionStatus === 'free') {
      showButton = false;
    } else {
      showButton = true;

      // When viewing another user's data, always show "View Data" and navigate to data screen
      if (isViewingOthersData) {
        if (isCompleted) {
          buttonText = 'View Data';
          buttonColor = '#FF9F5A';
          onPress = () => (navigation as any).navigate('Personal', {
            screen: task.dataScreen as any,
            params: { targetUserId: selectedUserId, viewOnly: true }
          });
        } else {
          // Task not completed by primary user - show disabled state
          buttonText = 'Not Completed';
          buttonColor = '#CCCCCC';
          onPress = () => {}; // No action for incomplete tasks when viewing others' data
        }
      } else if (isCompleted) {
        buttonText = 'Review Information';
        buttonColor = '#FF9F5A';
        onPress = () => (navigation as any).navigate('Personal', { screen: task.dataScreen as any });
      } else {
        buttonText = 'Click to Complete';
        buttonColor = '#29BFC0';
        onPress = () =>
          guardVaultEdit(canEditData, navigation, () =>
            (navigation as any).navigate('Personal', {
              screen: task.inputScreen,
              params: { taskId: task.id }
            }),
          );
      }
    }
    return (
      <TaskItem
        key={task.id}
        task={{ id: String(task.id), title: task.title }}
        isCompleted={isCompleted}
        onPress={onPress}
        hideActionButton={!showButton}
        buttonText={buttonText}
        buttonColor={buttonColor}
        subscriptionTier={subscriptionStatus}
        onCheckboxPress={() => {}}
        isDisabled={isTaskDisabled}
      />
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.mainContent}>
          <View style={styles.header}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
              accessibilityRole="button"
              accessibilityLabel="Go back"
              accessibilityHint="Double tap to go back to the previous screen"
            >
              <Image
                source={require('../../assets/icons/back.png')}
                style={styles.backIcon}
                accessibilityElementsHidden={true}
              />
            </TouchableOpacity>
            <Image
              source={require('../../assets/logo.png')}
              style={styles.logo}
              resizeMode="contain"
              accessibilityElementsHidden={true}
            />
          </View>
          <ActivityIndicator size="large" color="#29BFC0" style={{ marginTop: 50 }} accessibilityLabel="Loading" />
          <Text style={{ textAlign: 'center', marginTop: 20, color: '#666' }} accessibilityRole="text" accessibilityLiveRegion="polite">
            Loading Personal Checklist...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
            accessibilityRole="button"
            accessibilityLabel="Go back"
            accessibilityHint="Double tap to go back to the previous screen"
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
              accessibilityElementsHidden={true}
            />
          </TouchableOpacity>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
            accessibilityElementsHidden={true}
          />
          <View style={styles.headerPlaceholder} />
        </View>

        {isViewingOthersData && viewingUserName && (
          <View style={styles.viewingBanner} accessibilityRole="alert" accessibilityLabel={`Viewing ${viewingUserName}'s data in view only mode`}>
            <Text style={styles.viewingBannerText}>
              Viewing {viewingUserName}'s Data (View Only)
            </Text>
          </View>
        )}

        {/* Show upgrade banner for users without active subscription (only when viewing own data) */}
        {!isViewingOthersData && <UpgradeBanner variant="compact" />}

        <ChecklistCardScroll
          categories={mappedCategories}
          selectedCategory={'personal'}
          navigation={navigation}
          onCategoryPress={(categoryId) => {
            // Handle category navigation
            switch (categoryId) {
              case 'all':
                navigation.navigate('Dashboard', { screen: 'AllTasks' });
                break;
              case 'personal':
                // Already on personal checklist, do nothing
                break;
              case 'medical':
                navigation.navigate('Dashboard', { screen: 'MedicalChecklist' });
                break;
              case 'finance':
                navigation.navigate('Dashboard', { screen: 'FinanceChecklist' });
                break;
              case 'property':
                navigation.navigate('Dashboard', { screen: 'PropertyChecklist' });
                break;
              case 'finalWishes':
                navigation.navigate('Dashboard', { screen: 'FinalWishesChecklist' });
                break;
              case 'healthcareDirectives':
                navigation.navigate('Dashboard', { screen: 'HealthcareDirectivesChecklist' });
                break;
              case 'estatePlanning':
                navigation.navigate('Dashboard', { screen: 'EstatePlanningChecklist' });
                break;
              case 'carePreferences':
                navigation.navigate('Dashboard', { screen: 'CarePreferencesChecklist' });
                break;
              case 'passwords':
                navigation.navigate('Dashboard', { screen: 'OnlineAccountsChecklist' });
                break;
              case 'contacts':
                navigation.navigate('Dashboard', { screen: 'EmergencyContactsChecklist' });
                break;
              case 'expenses':
                navigation.navigate('Dashboard', { screen: 'ExpensesChecklist' });
                break;
              case 'documents':
                navigation.navigate('Dashboard', { screen: 'DocumentManagementChecklist' });
                break;
              case 'loyaltyPoints':
                navigation.navigate('Dashboard', { screen: 'LoyaltyPointsChecklist' });
                break;
            }
          }}
        />

        <Text style={styles.title} accessibilityRole="header">Personal Checklist</Text>

        <ScrollView
          style={styles.contentContainer}
          contentContainerStyle={{ paddingBottom: 200 }}
          accessibilityRole="list"
          accessibilityLabel="Personal checklist tasks"
        >
          {PERSONAL_TASKS_CONFIG.map(renderTaskItem)}
        </ScrollView>
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    paddingTop: ms(25),
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  logo: {
    width: ms(125, 0.3),
    height: ms(125, 0.3),
  },
  headerPlaceholder: {
    width: s(30),
    height: s(30),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginHorizontal: ms(15),
    marginVertical: ms(20),
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  viewingBanner: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(10),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(15),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewingBannerText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
}); 






































